import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { HardDrive, CheckCircle2, Link2, Unlink } from 'lucide-react'
import AppShell from '../components/AppShell.jsx'
import Card from '../components/ui/Card.jsx'
import Button from '../components/ui/Button.jsx'
import { useAuth } from '../lib/AuthContext.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { getDriveConnection, startDriveConnect, disconnectDrive } from '../lib/api.js'

export default function Settings() {
  const { profile } = useAuth()
  const toast = useToast()
  const [params] = useSearchParams()
  const [conn, setConn] = useState(undefined)
  const [busy, setBusy] = useState(false)

  async function refresh() {
    setConn(await getDriveConnection())
  }
  useEffect(() => {
    refresh()
    const status = params.get('drive')
    if (status === 'connected') toast('Google Drive backup connected', 'success')
    if (status === 'error') toast('Could not connect Google Drive — please try again', 'error')
  }, [])

  async function handleConnect() {
    setBusy(true)
    try {
      const url = await startDriveConnect(`FlipStudio-${profile?.organisations?.name || 'Backup'}`)
      window.location.href = url
    } catch (err) {
      toast(err.message, 'error')
      setBusy(false)
    }
  }
  async function handleDisconnect() {
    setBusy(true)
    try {
      await disconnectDrive()
      toast('Google Drive disconnected', 'success')
      refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      setBusy(false)
    }
  }

  const canManage = profile?.role === 'owner' || profile?.role === 'admin'

  return (
    <AppShell>
      <h1 className="font-display font-bold text-[24px] mb-1">Settings</h1>
      <p className="text-muted text-[14px] mb-8">Organisation and backup preferences.</p>

      <Card className="p-6 mb-6">
        <div className="font-semibold text-[15px] mb-1">Organisation</div>
        <div className="text-muted text-[13.5px]">{profile?.organisations?.name || '—'}</div>
      </Card>

      <Card className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-md bg-blue/10 flex items-center justify-center text-sky shrink-0">
            <HardDrive size={19} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] mb-1">Google Drive backup</div>
            <p className="text-muted text-[13.5px] mb-4">
              A free, best-effort disaster-recovery copy of every uploaded asset. Your books are always served from
              our primary storage — Drive is a safety net only, and never blocks an upload if it's unavailable.
            </p>

            {conn === undefined ? (
              <div className="h-9 w-40 rounded-md skeleton-shimmer" />
            ) : conn ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[13.5px]">
                  <CheckCircle2 size={16} className="text-ok" />
                  Connected as <span className="font-medium">{conn.connected_email}</span>
                </div>
                {canManage && (
                  <Button variant="ghost" size="sm" icon={Unlink} onClick={handleDisconnect} loading={busy}>
                    Disconnect
                  </Button>
                )}
              </div>
            ) : canManage ? (
              <Button icon={Link2} onClick={handleConnect} loading={busy}>Connect Google Drive</Button>
            ) : (
              <p className="text-muted-2 text-[13px]">Only an org owner or admin can connect Drive backup.</p>
            )}
          </div>
        </div>
      </Card>
    </AppShell>
  )
}
